/**
 * Pure function that builds the MongoDB update payload for a drag-and-drop
 * scheduling operation.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (for code reviewers asking "is this overkill?"):
 * --------------------------------------------------------------------------
 *
 * This is the same pattern as `buildScheduleUpdateFields` (exported from
 * JobsDetailModal.js) and `datesEqual` (src/utility/dateCompare.js): extract
 * the change-detection logic into a pure function so that tests import the
 * EXACT production code — no test-helper drift (#73, #80, #83, #84).
 *
 * --------------------------------------------------------------------------
 * THE BUG THIS HELPS PREVENT (#85):
 * --------------------------------------------------------------------------
 *
 * Before extraction, change-detection was inline inside `processDragNDrop`.
 * It compared the caller-supplied `updateValues` against `foundDocument` — but
 * `foundDocument` was a reference to a TanStack cache object that
 * `JobInitModal` had already mutated. So by the time comparison ran,
 * `updateValues.date_scheduled` and `foundDocument.date_scheduled` both held
 * the NEW value, `datesEqual` returned true, and EVERY changed field was
 * silently dropped from the payload. Users saw "it took 9 tries to save."
 *
 * The real fix was making `JobInitModal` mutation-free. This extraction is
 * the structural guard: it lets the test suite invoke the exact production
 * change-detection logic against an unmutated `foundDocument` and assert the
 * correct payload — so we catch any future regression of either the mutation
 * rule or the comparison logic.
 */

import { datesEqual } from "./dateCompare";

/**
 * Builds the updateFields payload for a drag-n-drop mutation.
 *
 * Contract:
 * - `history` and `_mongoUsername` are ALWAYS included.
 * - `rescheduleReason` is included only when `updateValues.rescheduleReason`
 *   is present (truthy).
 * - All other fields are included ONLY if their new value differs from
 *   `foundDocument`. Dates use semantic comparison (datesEqual) so
 *   "04/26/2026" vs "4/26/2026" is not treated as a change.
 * - `foundDocument` MUST NOT be mutated; `updateValues` MUST NOT be mutated.
 *
 * @param {Object} foundDocument - The current (server-state) project document.
 *   Any field may be missing. Must not be mutated.
 * @param {Object} updateValues - The new values from JobInitModal's
 *   pendingDragUpdate. Fields that are `undefined` fall back to
 *   `foundDocument`'s value (i.e., no-op change).
 * @param {string} history - The history log entry string.
 * @param {string} mongoUsername - The username to record with the change.
 * @returns {Object} updateFields object to send to the mutation.
 */
export function buildDragNDropUpdateFields(
  foundDocument,
  updateValues,
  history,
  mongoUsername
) {
  const safeUpdates = updateValues || {};
  const safeDoc = foundDocument || {};

  const updateFields = {
    history,
    _mongoUsername: mongoUsername,
  };

  if (safeUpdates.rescheduleReason) {
    updateFields.rescheduleReason = safeUpdates.rescheduleReason;
  }

  // Installer: string comparison (may have zero-pad differences — see #84 for potential future work)
  const newInstaller = safeUpdates.installer ?? safeDoc.installer;
  if (String(newInstaller ?? "") !== String(safeDoc.installer ?? "")) {
    updateFields.installer = newInstaller;
  }

  // Dates: semantic comparison to handle "04/26/2026" vs "4/26/2026" mismatch (#84)
  const newDateScheduled = safeUpdates.date_scheduled ?? safeDoc.date_scheduled;
  if (!datesEqual(newDateScheduled, safeDoc.date_scheduled)) {
    updateFields.date_scheduled = newDateScheduled;
  }
  const newEstComp =
    safeUpdates.date_estimatedCompletion ?? safeDoc.date_estimatedCompletion;
  if (!datesEqual(newEstComp, safeDoc.date_estimatedCompletion)) {
    updateFields.date_estimatedCompletion = newEstComp;
  }

  // Non-date string fields: plain comparison
  const newArrival =
    safeUpdates.arrival_time_text ?? safeDoc.arrival_time_text;
  if (String(newArrival ?? "") !== String(safeDoc.arrival_time_text ?? "")) {
    updateFields.arrival_time_text = newArrival;
  }

  // statusCode: numeric comparison (handles string/number coercion)
  const newStatusCode = safeUpdates.statusCode ?? safeDoc.statusCode;
  if (parseFloat(newStatusCode) !== parseFloat(safeDoc.statusCode)) {
    updateFields.statusCode = newStatusCode;
  }

  // extraCell fields: strict comparison
  const newExtraCellId = safeUpdates.extraCellId ?? safeDoc.extraCellId;
  if (newExtraCellId !== safeDoc.extraCellId) {
    updateFields.extraCellId = newExtraCellId;
  }
  const newExtraCellText = safeUpdates.extraCellText ?? safeDoc.extraCellText;
  if (newExtraCellText !== safeDoc.extraCellText) {
    updateFields.extraCellText = newExtraCellText;
  }

  return updateFields;
}
